import CodeBlockCard from "@/components/cards/message/CodeBlockCard";
import { LinkWithFavicon } from "@/components/cards/message/LinkWithFavicon";
import { useMemo } from "react";
import { BlockMath, InlineMath } from "react-katex";


export const useExtractMarkdownComponents = (role: string, threadId?: string) =>
    useMemo(
        () => ({
            code({ inline, className, children, ...props }: any) {
                const codeContent = String(children).replace(/\n$/, "");

                // Check if this is LaTeX content
                const isDisplayMath = codeContent.startsWith("$$") && codeContent.endsWith("$$");
                const isInlineMath =
                    codeContent.startsWith("$") && codeContent.endsWith("$") && !codeContent.includes("\n");
                const hasLatexCommands = /\\[a-zA-Z]+/.test(codeContent);

                // Handle LaTeX rendering
                if (isDisplayMath) {
                    const latex = codeContent.slice(2, -2).trim();
                    try {
                        return (
                            <div className="my-4 text-center">
                                <BlockMath math={latex} />
                            </div>
                        );
                    } catch (error) {
                        console.warn("LaTeX rendering error:", error);
                        return (
                            <div className="my-4 rounded border border-red-200 bg-red-50 p-2 text-center">
                                <code className="text-red-600">LaTeX Error: {latex}</code>
                            </div>
                        );
                    }
                }

                if (isInlineMath && codeContent.length > 2) {
                    const latex = codeContent.slice(1, -1).trim();
                    try {
                        return <InlineMath math={latex} />;
                    } catch (error) {
                        console.warn("LaTeX rendering error:", error);
                        return (
                            <code className="rounded bg-red-50 px-1 py-0.5 text-xs text-red-600">
                                LaTeX Error: {latex}
                            </code>
                        );
                    }
                }

                // If it contains LaTeX commands but isn't wrapped in $, try to render as LaTeX
                if (hasLatexCommands && !className?.includes("language-")) {
                    try {
                        return (
                            <div className="my-4 text-center">
                                <BlockMath math={codeContent} />
                            </div>
                        );
                    } catch (error) {
                        console.warn("LaTeX rendering error:", error);
                    }
                }

                // Force inline rendering for short code snippets
                const shouldBeInline =
                    inline ||
                    (codeContent.length <= 50 &&
                        !codeContent.includes("\n") &&
                        !codeContent.includes(";") &&
                        codeContent.split(" ").length <= 3);

                if (shouldBeInline) {
                    return (
                        <code
                            className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                            {...props}
                        >
                            {children}
                        </code>
                    );
                }

                // Render as code block for longer content
                const match = /language-(\w+)/.exec(className || "");
                const language = match ? match[1] : "text";
                return <CodeBlockCard language={language} code={codeContent} />;
            },

            // Simplified p component without artifact button handling
            p({ children, ...props }) {
                return <p {...props}>{children}</p>;
            },

            a: (props: any) => {
                if (role === "assistant") {
                    return <LinkWithFavicon {...props} threadId={threadId} />;
                }
                return (
                    <a target="_blank" rel="noreferrer" className="text-primary underline" {...props}>
                        {props.children}
                    </a>
                );
            },

            img({ src, alt, ...props }: any) {
                return (
                    <img
                        src={src}
                        alt={alt || "Image"}
                        loading="lazy"
                        className="my-6 w-full rounded-md shadow-sm"
                        {...props}
                    />
                );
            },
        }),
        [role, threadId]
    );
